import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import {
  CODE_PREFIX,
  customKey,
  SPEC_PARAMS,
  SPEC_PRESETS,
  specParam,
  type SpecField,
  type SpecKey,
} from '@zilian/contracts';
import { api } from './api';
import type { Category } from './SkuCreateDialog';

const standard = Object.keys(SPEC_PARAMS) as SpecKey[];
/** The standard parameter a name stands for, unless another row already is it. */
const standardNamed = (label: string, fields: SpecField[]) =>
  standard.find((k) => SPEC_PARAMS[k].label === label.trim() && !fields.some((f) => f.key === k));

interface Draft {
  id: string | null;
  name: string;
  codePrefix: string;
  fields: SpecField[];
}

/**
 * The enterprise's product categories and their spec templates. A category starts from a
 * platform preset or blank; its parameters, usual values and order are the enterprise's own.
 */
export default function SpecTemplatesDialog(p: {
  categories: Category[];
  skus: any[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const used = (id: string) => p.skus.filter((s) => s.categoryId === id).length;
  const presets = SPEC_PRESETS.filter((x) => !p.categories.some((c) => c.name === x.name));

  const start = (d: Draft) => {
    setMenu(null);
    setError('');
    setDraft(structuredClone(d));
  };
  const setField = (i: number, patch: Partial<SpecField>) =>
    setDraft(
      (d) => d && { ...d, fields: d.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) },
    );

  async function run(action: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError('');
    try {
      await action();
      setDraft(null);
      p.onSaved(message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (draft) {
    // What the system does with a parameter it computes with, under its name.
    const hint = (field: SpecField) =>
      specParam(field.key || standardNamed(field.label, draft.fields) || '')?.hint;
    const save = () => {
      if (!CODE_PREFIX.test(draft.codePrefix))
        return setError('编码前缀请填 2–4 个英文字母，如 CAP');
      if (draft.fields.some((f) => !f.label.trim()))
        return setError('请填写参数名称，或删除空白参数');
      // A new row is the standard parameter it is named after, else one of the enterprise's
      // own. A key once given never changes, so renaming keeps the values.
      const fields: SpecField[] = [];
      for (const f of draft.fields)
        fields.push({
          ...f,
          label: f.label.trim(),
          key: f.key || standardNamed(f.label, [...draft.fields, ...fields]) || customKey(),
        });
      run(
        () =>
          api(draft.id ? `/categories/${draft.id}` : '/categories', {
            name: draft.name,
            codePrefix: draft.codePrefix,
            fields,
          }),
        draft.id ? `已保存「${draft.name}」规格模板` : `已添加分类「${draft.name}」`,
      );
    };
    return (
      <Dialog open fullWidth maxWidth="md" onClose={() => !busy && setDraft(null)}>
        <DialogTitle>{draft.id ? '编辑规格模板' : '添加商品分类'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'flex-start' }}>
            <TextField
              label="分类名称"
              value={draft.name}
              disabled={busy}
              sx={{ width: 260 }}
              slotProps={{ htmlInput: { maxLength: 20 } }}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <TextField
              label="编码前缀"
              placeholder="如 CAP"
              value={draft.codePrefix}
              disabled={busy}
              sx={{ width: 260 }}
              // Each item of the category is numbered under it; existing codes never change.
              helperText={`新建商品编码为 ${draft.codePrefix || 'CAP'}-0001 起`}
              slotProps={{
                htmlInput: { maxLength: 4, style: { textTransform: 'uppercase' } },
                formHelperText: { sx: { mx: 0 } },
              }}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  codePrefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, ''),
                })
              }
            />
          </Box>
          <Table size="small" sx={{ mt: 2, '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ width: 150 }}>参数</TableCell>
                <TableCell>常用值（回车添加）</TableCell>
                <TableCell align="center" sx={{ width: 56 }}>
                  必填
                </TableCell>
                <TableCell sx={{ width: 76 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.fields.map((field, i) => (
                <TableRow key={i} sx={{ verticalAlign: 'top', '& td': { py: 1 } }}>
                  <TableCell>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      size="small"
                      // Standard parameters not yet in the template; any other name is typed.
                      options={standard
                        .filter((k) => k === field.key || !draft.fields.some((f) => f.key === k))
                        .map((k) => SPEC_PARAMS[k].label)}
                      value={field.label}
                      inputValue={field.label}
                      disabled={busy}
                      onInputChange={(_, label, reason) =>
                        reason === 'input' && setField(i, { label: label.slice(0, 10) })
                      }
                      onChange={(_, label) => {
                        const key = standard.find((k) => SPEC_PARAMS[k].label === label);
                        if (!key) return setField(i, { label });
                        // Picking a standard parameter makes the row that parameter.
                        setField(i, {
                          key,
                          label,
                          options: field.options.length
                            ? field.options
                            : [...SPEC_PARAMS[key].options],
                        });
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="如：口径" />}
                    />
                    {hint(field) && (
                      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                        {hint(field)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={[]}
                      value={field.options}
                      disabled={busy}
                      onChange={(_, value) =>
                        setField(i, {
                          options: [...new Set(value.map((v) => v.trim()).filter(Boolean))],
                        })
                      }
                      renderValue={(value, getItemProps) =>
                        value.map((option, index) => {
                          const { key, ...props } = getItemProps({ index });
                          return <Chip key={key} label={option} size="small" {...props} />;
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={field.options.length ? '' : '没有常用值时建档直接填写'}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={field.required}
                      disabled={busy}
                      onChange={(e) => setField(i, { required: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="上移">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy || i === 0}
                          onClick={() => {
                            const fields = [...draft.fields];
                            [fields[i - 1], fields[i]] = [fields[i], fields[i - 1]];
                            setDraft({ ...draft, fields });
                          }}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="删除参数">
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          setDraft({ ...draft, fields: draft.fields.filter((_, j) => j !== i) })
                        }
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            startIcon={<Add />}
            disabled={busy || draft.fields.length >= 20}
            sx={{ mt: 1 }}
            onClick={() =>
              setDraft({
                ...draft,
                fields: [...draft.fields, { key: '', label: '', options: [], required: false }],
              })
            }
          >
            添加参数
          </Button>
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
            关键规格按参数顺序生成。修改模板不会改动已建商品，编辑商品时按新模板填写。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {draft.id && (
            <Button
              color="error"
              disabled={busy}
              sx={{ mr: 'auto' }}
              onClick={() =>
                run(() => api(`/categories/${draft.id}/delete`, {}), `已删除分类「${draft.name}」`)
              }
            >
              删除分类
            </Button>
          )}
          <Button disabled={busy} onClick={() => setDraft(null)}>
            返回
          </Button>
          <Button variant="contained" disabled={busy} onClick={save}>
            {busy ? '正在保存…' : '保存模板'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={p.onClose}>
      <DialogTitle>规格模板</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
          按商品分类设置规格参数和常用值，新建商品时点选即可，关键规格自动生成。
        </Typography>
        {p.categories.length ? (
          <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>分类</TableCell>
                <TableCell>编码前缀</TableCell>
                <TableCell>规格参数</TableCell>
                <TableCell align="right">商品</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {p.categories.map((c) => (
                <TableRow key={c.id}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{c.name}</TableCell>
                  <TableCell>{c.codePrefix}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {c.fields.map((f) => f.label).join('、') || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {used(c.id)}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>
                    <Button size="small" onClick={() => start(c)}>
                      编辑
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 14 }}>
            还没有商品分类，可从行业模板开始
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button startIcon={<Add />} sx={{ mr: 'auto' }} onClick={(e) => setMenu(e.currentTarget)}>
          添加分类
        </Button>
        <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
          {presets.map((x) => (
            <MenuItem
              key={x.name}
              onClick={() =>
                start({
                  id: null,
                  name: x.name,
                  // The preset's prefix, unless another category of this enterprise has it.
                  codePrefix: p.categories.some((c) => c.codePrefix === x.prefix) ? '' : x.prefix,
                  fields: x.fields,
                })
              }
            >
              {x.name}模板
            </MenuItem>
          ))}
          <MenuItem onClick={() => start({ id: null, name: '', codePrefix: '', fields: [] })}>
            空白分类
          </MenuItem>
        </Menu>
        <Button onClick={p.onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
}
